import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, of } from 'rxjs';

import { EnterpriseProxy, IEnterprisePermissionsViewModel } from '../../proxy/proxies';

interface PermissionCache {
    permissionsByEnterprises: IEnterprisePermissionsViewModel[];
    userPermissions: string[];
}

@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    private readonly enterpriseProxy = inject(EnterpriseProxy);

    private readonly permissionsCache = new BehaviorSubject<PermissionCache>({
        permissionsByEnterprises: [],
        userPermissions: [],
    });
    private readonly permissionSubject = new BehaviorSubject<PermissionCache | null>(null);
    readonly permission$ = this.permissionSubject.asObservable();

    async initializeUserPermissions(enterpriseIds: string[], userPermissions: string[]): Promise<void> {
        if (!enterpriseIds?.length) {
            console.warn('No enterprise IDs provided for permission initialization');
            this.updatePermissionsCache({
                permissionsByEnterprises: [],
                userPermissions: userPermissions ?? [],
            });
            return;
        }

        const enterprises = await firstValueFrom(
            this.enterpriseProxy.enterprise_GetPermissionsByEnterprises(enterpriseIds).pipe(
                catchError((error) => {
                    console.error('Failed to initialize user permissions:', error);
                    return of(null);
                }),
            ),
        );

        if (enterprises?.isSuccess && enterprises.result) {
            this.updatePermissionsCache({
                permissionsByEnterprises: enterprises.result.map((e: IEnterprisePermissionsViewModel) => ({
                    enterpriseId: e.enterpriseId,
                    permissions: e.permissions,
                })),
                userPermissions: userPermissions ?? [],
            });
        }
    }

    private updatePermissionsCache(data: Partial<PermissionCache>): void {
        const currentCache = this.permissionsCache.value;
        const newCache: PermissionCache = {
            ...currentCache,
            ...data,
        };
        this.permissionsCache.next(newCache);
        this.permissionSubject.next(newCache);
    }

    clearPermissionsCache(): void {
        this.permissionsCache.next({ permissionsByEnterprises: [], userPermissions: [] });
        this.permissionSubject.next(null);
    }

    /** Logout / session reset (alias for Doktar parity). */
    clearPermissions(): void {
        this.clearPermissionsCache();
    }

    isGrantedByEnterprise(enterpriseId: string, code: string): boolean {
        const enterpriseBasedPermissions = this.permissionsCache.value.permissionsByEnterprises;
        const enterprise = enterpriseBasedPermissions.find((p) => p.enterpriseId === enterpriseId);
        return enterprise?.permissions?.includes(code) || false;
    }

    isGrantedOnUserPermissions(code: string): boolean {
        const userPermissions = this.permissionsCache.value.userPermissions;
        return userPermissions.map((e) => e.toLowerCase()).includes(code.toLowerCase()) || false;
    }

    isFullyGranted(code: string, currentEnterpriseId: string | undefined): boolean {
        if (!currentEnterpriseId) {
            return this.isGrantedOnUserPermissions(code);
        }
        return (
            this.isGrantedByEnterprise(currentEnterpriseId, code) &&
            this.isGrantedOnUserPermissions(code)
        );
    }
}
